import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { IrisAppService } from './iris-app.service';
import { Web3Service, resolveLegacyUserInfoAddress } from './web3Service';

// Même forme que IrisUserPublicDto côté API (voir IrisUsersController.ToPublicDto) — c'est
// TOUJOURS ce DTO qui est mis en cache sous la clé 'playerInfo', jamais autre chose.
export interface Account {
  Id: number;
  Points: number;
  PointsUsdt: number;
  ReferralCode: string | null;
  InvitedBy: number | null;
  UserAddress: string | null;
  IsPremium: boolean;
  IsSpecialHolder: boolean;
  ReferralRewarded: boolean;
  PremiumRewarded: boolean;
  SpecialHolderRewarded: boolean;
}

// AccountService (2026-08-20) — SEULE source de vérité pour le wallet connecté (Preferences
// 'UserConnection') et le compte IrisApp qui lui est lié (Preferences 'playerInfo').
//
// Pourquoi ce service existe : avant lui, ~32 pages lisaient/écrivaient ces deux clés Preferences
// chacune à sa façon, avec des logiques légèrement différentes de résolution "quel compte
// correspond à ce wallet" (choose-wallet.page.ts avait son linkWalletDeferred, verification.page.ts
// une variante proche mais pas identique, leaderboard.page.ts une 3e). Résultat : changer de wallet
// laissait souvent "Mes Amis"/les récompenses affichées pointer encore vers l'ANCIEN compte, le
// temps qu'une des multiples implémentations partielles se déclenche (ou jamais, si son cas
// particulier n'était pas couvert). Toute la logique de synchronisation vit maintenant ICI, une
// seule fois — les pages ne font plus que lire l'état exposé (getAddress/getAccount, ou les
// Observables address$/account$) ou appeler setAddress()/disconnect().
//
// Règle centrale demandée explicitement (2026-08-20) : dès que l'adresse connectée change, on vide
// D'ABORD le compte en cache (playerInfo, qui appartenait à l'ANCIEN wallet) avant de tenter de
// résoudre le compte du NOUVEAU wallet — jamais un mélange des deux, jamais l'ancien compte affiché
// avec la nouvelle adresse.
@Injectable({ providedIn: 'root' })
export class AccountService {
  private addressSubject = new BehaviorSubject<string>('');
  private accountSubject = new BehaviorSubject<Account | null>(null);
  readonly address$ = this.addressSubject.asObservable();
  readonly account$ = this.accountSubject.asObservable();

  private initPromise?: Promise<void>;

  // Id du compte actif juste AVANT le dernier changement d'adresse, quand ce changement n'a résolu
  // aucun compte déjà lié (GetByWallet vide) — voir setAddress()/linkWallet() ci-dessous. Permet à
  // linkWallet() de savoir QUEL compte relier même après que setAddress() ait vidé accountSubject.
  private pendingLinkAccountId: number | null = null;

  constructor(
    private irisApp: IrisAppService,
    private web3: Web3Service,
  ) {}

  // Angular n'autorise pas de constructeur async — l'état persistant (Preferences) est chargé
  // paresseusement au premier accès, une seule fois (initPromise partagée pour ne jamais relire
  // Preferences deux fois en parallèle au tout premier appel concurrent).
  private ensureInit(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = (async () => {
        const [addrPref, infoPref, userInfoPref] = await Promise.all([
          Preferences.get({ key: 'UserConnection' }),
          Preferences.get({ key: 'playerInfo' }),
          Preferences.get({ key: 'userInfo' }),
        ]);
        // Repli sur l'ancien flux "code d'activation" (clé 'userInfo', contient { Address, ... })
        // si 'UserConnection' est vide — même repli que ~6 pages le faisaient chacune à leur façon
        // via resolveLegacyUserInfoAddress(), centralisé ici une bonne fois pour toutes.
        const address = addrPref.value || resolveLegacyUserInfoAddress(userInfoPref.value);
        if (address) this.addressSubject.next(address);
        if (infoPref.value) {
          try { this.accountSubject.next(JSON.parse(infoPref.value)); } catch { /* cache corrompu */ }
        }
      })();
    }
    return this.initPromise;
  }

  async getAddress(): Promise<string> {
    await this.ensureInit();
    return this.addressSubject.value;
  }

  async getAccount(): Promise<Account | null> {
    await this.ensureInit();
    return this.accountSubject.value;
  }

  get currentAddress(): string { return this.addressSubject.value; }
  get currentAccount(): Account | null { return this.accountSubject.value; }

  // Point d'entrée UNIQUE pour enregistrer une adresse wallet fraîchement connectée — remplace
  // tous les Preferences.set({key:'UserConnection', value: address}) dispersés dans l'app. Si
  // l'adresse est réellement différente de celle déjà en cache, vide immédiatement le compte
  // (ancien wallet) puis tente de résoudre le compte du nouveau wallet (GetByWallet). Idempotent :
  // rappeler avec la même adresse ne fait rien de plus que réécrire la même valeur.
  async setAddress(address: string): Promise<Account | null> {
    await this.ensureInit();
    const changed = address.toLowerCase() !== this.addressSubject.value.toLowerCase();
    // Capturé AVANT de vider — voir repli plus bas (2026-08-20, fix régression).
    const previousAccount = this.accountSubject.value;

    await Preferences.set({ key: 'UserConnection', value: address });
    this.addressSubject.next(address);

    if (!changed) return this.accountSubject.value;

    // Vide D'ABORD — mieux vaut afficher "pas de compte" un court instant que continuer à montrer
    // les points/amis/lien de parrainage d'un AUTRE wallet pendant la résolution ci-dessous.
    await Preferences.remove({ key: 'playerInfo' });
    this.accountSubject.next(null);

    const found = await this.resolveForAddress(address);
    if (found) { this.pendingLinkAccountId = null; return found; }

    // FIX RÉGRESSION (2026-08-20) : GetByWallet ne trouve rien pour une adresse JAMAIS liée à un
    // compte (cas normal du tout premier "Connecter mon wallet" après une inscription Telegram —
    // voir home.page.ts:ajouterUtilisateurTelegram, qui crée le compte mais ne lie jamais de
    // wallet). setAddress() reste volontairement SANS signature (appelée par ~28 pages juste pour
    // enregistrer l'adresse — une demande de signature surprise à chaque connexion serait une
    // régression UX pire que le bug initial) : on retient juste QUEL compte relier, pour que
    // linkWallet() (appelé explicitement par les pages qui en ont besoin — choose-wallet,
    // verification, connectWalletForReferral) sache le retrouver même après ce vidage.
    this.pendingLinkAccountId = previousAccount?.Id ?? null;
    return null;
  }

  private async resolveForAddress(address: string): Promise<Account | null> {
    const found = await firstValueFrom(this.irisApp.getIrisUserByWallet(address)).catch(() => null) as Account | null;
    if (found?.Id) {
      await Preferences.set({ key: 'playerInfo', value: JSON.stringify(found) });
      this.accountSubject.next(found);
      return found;
    }
    return null;
  }

  // Enregistre un compte obtenu autrement qu'via setAddress/ensureFresh (inscription Telegram via
  // postUserInfo dans home.page.ts, réponse d'un endpoint IrisUsers/*...) — remplace les
  // Preferences.set({key:'playerInfo'...}) dispersés dans chaque page.
  async setAccount(account: Account): Promise<void> {
    await this.ensureInit();
    await Preferences.set({ key: 'playerInfo', value: JSON.stringify(account) });
    this.accountSubject.next(account);
  }

  // Relie le wallet ACTUELLEMENT connecté au compte ACTUELLEMENT en cache côté serveur (PUT
  // /wallet — crée/retrouve la ligne RewardWallet, signature requise) — remplace les deux copies
  // quasi identiques de linkWalletDeferred (choose-wallet.page.ts et verification.page.ts).
  // Fire-and-forget côté appelant recommandé (ne bloque jamais une navigation déjà en cours) :
  // un échec ici (signature refusée, wallet lent) n'empêche jamais d'avoir déjà le ReferralCode
  // via setAddress()/ensureFresh() ci-dessus, qui ne dépend pas de cette signature.
  async linkWallet(isPremium: boolean, isSpecialHolder: boolean): Promise<Account | null> {
    await this.ensureInit();
    const address = this.addressSubject.value;
    // pendingLinkAccountId : repli (2026-08-20) pour le cas où setAddress() vient de vider
    // accountSubject sans rien trouver via GetByWallet — voir son commentaire.
    const accountId = this.accountSubject.value?.Id ?? this.pendingLinkAccountId;
    if (!address || !accountId) return null;
    const updated = await this.updateWalletFor(accountId, address, isPremium, isSpecialHolder);
    if (updated) this.pendingLinkAccountId = null;
    return updated;
  }

  // Cœur commun de linkWallet() ci-dessus et du repli dans setAddress() — signature requise
  // (EIP-191, voir Web3Service.signWalletAuthHeaders), donc jamais silencieux : si l'appelant est
  // en contexte "fire-and-forget", il doit lui-même avaler l'erreur (voir call sites).
  private async updateWalletFor(accountId: number, address: string, isPremium: boolean, isSpecialHolder: boolean): Promise<Account | null> {
    const headers = await this.web3.signWalletAuthHeaders();
    const updated = await firstValueFrom(
      this.irisApp.updateWallet(accountId, address, isPremium, isSpecialHolder, headers)
    ) as Account;
    if (updated) await this.setAccount(updated);
    return updated ?? null;
  }

  // Filet de sécurité (2026-08-23) — même principe que retryReferralCreditIfNeeded() dans
  // earn.page.ts, mais appliqué à la LIAISON du wallet elle-même (PUT /wallet), pas seulement au
  // crédit Premium après stake. But : rendre le parcours 100% Telegram (connexion + parrainage)
  // fiable sans intervention — si linkWallet() n'a jamais abouti pour une raison quelconque
  // (course au démarrage — voir home.page.ts:ajouterUtilisateurTelegram —, signature ratée/
  // ignorée, coupure réseau...), on retente ICI en silence à chaque appel (pages qui l'invoquent :
  // home, leaderboard). UNIQUEMENT si une signature valide est DÉJÀ en cache
  // (Web3Service.getCachedWalletAuthHeaders ne déclenche jamais de popup) : jamais de demande de
  // signature surprise hors de toute action explicite de l'utilisateur.
  async retryLinkIfNeeded(): Promise<void> {
    await this.ensureInit();
    const address = this.addressSubject.value;
    const account = this.accountSubject.value;
    // Rien à faire : pas de wallet connecté, pas de compte à lier, ou déjà lié.
    if (!address || !account || account.UserAddress) return;

    const headers = await this.web3.getCachedWalletAuthHeaders(address);
    if (!headers) return; // pas de signature en cache — on ne force rien, on retentera plus tard

    try {
      const updated = await firstValueFrom(
        this.irisApp.updateWallet(account.Id, address, account.IsPremium, account.IsSpecialHolder, headers)
      ) as Account;
      if (updated) await this.setAccount(updated);
    } catch { /* silencieux — retentera à la prochaine visite */ }
  }

  // Vide UNIQUEMENT le compte en cache (playerInfo), garde l'adresse connectée telle quelle — cas
  // d'un 404 après fusion de comptes côté serveur (même wallet reconnecté sous un autre compte
  // local, l'Id en cache a disparu) : le wallet est toujours bien connecté, seul le compte associé
  // doit être rerésolu (voir invite-friends.page.ts). Distinct de disconnect() ci-dessous, qui vide
  // aussi l'adresse.
  async clearAccount(): Promise<void> {
    await this.ensureInit();
    await Preferences.remove({ key: 'playerInfo' });
    this.accountSubject.next(null);
  }

  // Déconnexion — vide TOUT l'état de session wallet/compte d'un coup (remplace les paires
  // Preferences.remove({key:'UserConnection'}) + ({key:'playerInfo'}) dispersées).
  async disconnect(): Promise<void> {
    await this.ensureInit();
    await Preferences.remove({ key: 'UserConnection' });
    await Preferences.remove({ key: 'playerInfo' });
    // userInfo : ancien flux "code d'activation" retiré depuis longtemps, gardé en repli dans
    // quelques pages pour l'adresse wallet — nettoyé ici pour qu'une valeur périmée ne resurgisse
    // pas après déconnexion (voir choose-wallet.page.ts, comportement conservé).
    await Preferences.remove({ key: 'userInfo' });
    this.addressSubject.next('');
    this.accountSubject.next(null);
  }
}
